import { Box, SelectChangeEvent, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import FourImagesIcon from "../../../../assets/Fibo/icons/FourImagesIcon";
import OneImageIcon from "../../../../assets/Fibo/icons/OneImageIcon";
import ThreeImagesIcon from "../../../../assets/Fibo/icons/ThreeImagesIcon";
import TowImagesIcon from "../../../../assets/Fibo/icons/TowImagesIcon";
import BriaDropDown, { DropDownItem } from "../../../../components/common/BriaDropDown/BriaDropDown";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip";
import { FIBO_DEFAULT_NUM_RESULTS } from "../../../../constants/FiboConstants";
import styles from "./FiboNumberOfImages.module.scss";

interface FiboNumberOfImagesProps {
	value: number | undefined;
	onChange: (value: number) => void;
}

const FiboNumberOfImages = ({ value = FIBO_DEFAULT_NUM_RESULTS, onChange }: FiboNumberOfImagesProps) => {
	const { t } = useTranslation("fibo", { keyPrefix: "lab.settings.numberOfImages" });
	const items = [1, 2, 3, 4].map((n) => ({ value: n, key: String(n) }));
	const icons = [<OneImageIcon />, <TowImagesIcon />, <ThreeImagesIcon />, <FourImagesIcon />];

	const renderCustomItem = (item: DropDownItem<number>, showTranslation: boolean = true) => (
		<Box className={styles.aspectRatioItem}>
			<Box className={styles.aspectRatioValueContainer}>
				<Box className={styles.aspectRatioIconContainer}>{icons[item.value - 1]}</Box>
				<Typography className={styles.aspectRatioValue}>
					{showTranslation ? t(`${item.value}`) : item.value}
				</Typography>
			</Box>
		</Box>
	);

	const customItems = items.reduce<DropDownItem<number>[]>((acc, item) => {
		acc.push({
			...item,
			customItem: renderCustomItem(item as DropDownItem<number>),
			customSelectedKey: renderCustomItem(item as DropDownItem<number>, false),
		} as DropDownItem<number>);
		return acc;
	}, []);

	const handleNumberOfImagesChange = (e: SelectChangeEvent<number>) => {
		const newValue = e.target.value as number;
		onChange(newValue);
	};

	return (
		<BriaTooltip title={t("tooltip")}>
			<BriaDropDown<number>
				items={customItems}
				value={value}
				onChange={handleNumberOfImagesChange}
				placeholder={t("placeholder")}
				hideArrowIcon
				componentsProps={{
					root: { className: styles.controlButton },
				}}
			/>
		</BriaTooltip>
	);
};

export default observer(FiboNumberOfImages);
